import React, { useState, useCallback } from 'react'
import { ArrowRight, Loader2, Plus, Hash } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { upsertIdentity } from '@/features/company-profile'
import { INDUSTRIES, TEAM_SIZES } from '../types'
import type { WorkspaceData } from '../types'
import { createWorkspace, joinWorkspace, type WorkspaceSummary } from '../services/workspaceApi'

interface Props {
  workspace: WorkspaceData
  onUpdate: (data: Partial<WorkspaceData>) => void
  onCreated: (workspace: WorkspaceSummary) => void
}

type Mode = 'create' | 'join'

function formatJoiningId(raw: string): string {
  const clean = raw.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12)
  const parts = [clean.slice(0, 4), clean.slice(4, 8), clean.slice(8, 12)].filter(Boolean)
  return parts.join('-')
}

const WorkspaceSetup: React.FC<Props> = ({ workspace, onUpdate, onCreated }) => {
  const { currentTheme } = useTheme()
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('create')
  const [joiningId, setJoiningId] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleModeSwitch = useCallback((next: Mode) => {
    setMode(next)
    setError('')
  }, [])

  const handleJoiningIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setJoiningId(formatJoiningId(e.target.value))
    setError('')
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError('')

      if (mode === 'create') {
        if (!workspace.workspaceName.trim()) {
          setError('Workspace name is required.')
          return
        }
        if (workspace.workspaceName.trim().length < 2) {
          setError('Workspace name must be at least 2 characters.')
          return
        }
        if (!workspace.industry) {
          setError('Industry is required.')
          return
        }
        if (!workspace.teamSize) {
          setError('Team size is required.')
          return
        }
        setLoading(true)
        try {
          const created = await createWorkspace(workspace.workspaceName.trim())
          try {
            await upsertIdentity(created.id, { industry: workspace.industry, companySize: workspace.teamSize })
          } catch {
            // Workspace already exists at this point — don't block the flow over
            // this secondary write, the user can fill industry/size in later via Settings.
          }
          onCreated(created)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to create workspace. Please try again.')
        } finally {
          setLoading(false)
        }
      } else {
        const clean = joiningId.replace(/-/g, '')
        if (clean.length !== 12) {
          setError('Please enter a valid invite code (format: xxxx-xxxx-xxxx).')
          return
        }
        setLoading(true)
        try {
          await joinWorkspace(joiningId)
          navigate('/dashboard', { replace: true })
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to join workspace. Please try again.')
        } finally {
          setLoading(false)
        }
      }
    },
    [mode, workspace.workspaceName, workspace.industry, workspace.teamSize, joiningId, navigate, onCreated],
  )

  const inputStyle = {
    backgroundColor: currentTheme.inputBackground,
    color: currentTheme.text,
    borderColor: currentTheme.border,
  }

  return (
    <div className="p-6 sm:p-8 xl:p-10 flex flex-col h-full">
      <div className="mb-6">
        {mode === 'create' && (
          <span
            className="inline-block mb-3 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase"
            style={{ backgroundColor: `${currentTheme.accent}18`, color: currentTheme.accent }}
          >
            Step 1 of 2
          </span>
        )}
        <h1 className="text-3xl font-semibold sm:text-4xl" style={{ color: currentTheme.text }}>
          {mode === 'create' ? 'Set up your workspace' : 'Join a workspace'}
        </h1>
        <p className="mt-2 text-base leading-7" style={{ color: currentTheme.secondrytext }}>
          {mode === 'create'
            ? 'Create a new workspace for your team.'
            : 'Enter the invite code shared by your workspace admin.'}
        </p>
      </div>

      {/* Mode toggle */}
      <div
        className="flex rounded-2xl p-1 mb-6 gap-1"
        style={{ backgroundColor: currentTheme.hoverBackground }}
      >
        {(['create', 'join'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => handleModeSwitch(m)}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-all duration-200"
            style={{
              backgroundColor: mode === m ? currentTheme.secondary : 'transparent',
              color: mode === m ? currentTheme.text : currentTheme.mutedText,
              boxShadow: mode === m ? currentTheme.boxShadow : 'none',
            }}
          >
            {m === 'create' ? <Plus size={14} /> : <Hash size={14} />}
            {m === 'create' ? 'Create workspace' : 'Join workspace'}
          </button>
        ))}
      </div>

      {error && (
        <div
          className="mb-5 rounded-2xl px-5 py-4 text-sm"
          style={{
            backgroundColor: currentTheme.errorBackground,
            color: currentTheme.errorText,
            border: `1px solid ${currentTheme.border}`,
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5 flex-1">
        {mode === 'create' ? (
          <>
            <div>
              <label
                className="block text-sm font-semibold mb-2"
                style={{ color: currentTheme.text }}
              >
                Workspace name <span style={{ color: currentTheme.accent }}>*</span>
              </label>
              <input
                value={workspace.workspaceName}
                onChange={(e) => {
                  onUpdate({ workspaceName: e.target.value })
                  setError('')
                }}
                placeholder="e.g. Acme Sales Team"
                className="w-full rounded-2xl border px-4 py-3 text-sm outline-none transition"
                style={inputStyle}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: currentTheme.text }}
                >
                  Industry <span style={{ color: currentTheme.accent }}>*</span>
                </label>
                <select
                  value={workspace.industry}
                  onChange={(e) => onUpdate({ industry: e.target.value })}
                  className="w-full rounded-2xl border px-4 py-3 text-sm outline-none transition appearance-none"
                  style={inputStyle}
                >
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: currentTheme.text }}
                >
                  Team size <span style={{ color: currentTheme.accent }}>*</span>
                </label>
                <select
                  value={workspace.teamSize}
                  onChange={(e) => onUpdate({ teamSize: e.target.value })}
                  className="w-full rounded-2xl border px-4 py-3 text-sm outline-none transition appearance-none"
                  style={inputStyle}
                >
                  <option value="">Select team size</option>
                  {TEAM_SIZES.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        ) : (
          <div>
            <label
              className="block text-sm font-semibold mb-2"
              style={{ color: currentTheme.text }}
            >
              Invite code <span style={{ color: currentTheme.accent }}>*</span>
            </label>
            <input
              value={joiningId}
              onChange={handleJoiningIdChange}
              placeholder="xxxx-xxxx-xxxx"
              maxLength={14}
              spellCheck={false}
              autoComplete="off"
              className="w-full rounded-2xl border px-4 py-3 text-sm outline-none transition font-mono tracking-widest"
              style={inputStyle}
            />
            <p className="mt-2 text-xs" style={{ color: currentTheme.mutedText }}>
              Ask your workspace admin for the invite code from their workspace settings.
            </p>
          </div>
        )}

        <div className="mt-auto pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: currentTheme.sendButtonPrimary,
              color: currentTheme.buttonText,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight size={16} />}
            <span>
              {loading
                ? mode === 'create' ? 'Creating…' : 'Joining…'
                : mode === 'create' ? 'Continue' : 'Join workspace'}
            </span>
          </button>
        </div>
      </form>
    </div>
  )
}

export default React.memo(WorkspaceSetup)
